import React, { useEffect, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import './doctors.css';
import api from '../../service/caller';
import AppointmentPatientBarChart from '../../components/AppointmentBarChart';



export default function Dashboard({ doctor }) {
  // Données des 6 cartes initiales

  const [appointmentData, setAppointmentData] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const [distributionData, setDistributionData] = useState([]);
  const [appointmentSummary, setAppointmentSummary] = useState([]);
  const [patientStatus, setPatientStatus] = useState([]);
  const [insights, setInsights] = useState([]);
  const [cancelledAppointment, setCancelledAppointment] = useState([]);
  const [cancelledAppointmentByDepartment, setCancelledAppointmentByDepartment] = useState([]);
  const [cancelledAppointmentByConsultType, setCancelledAppointmentByConsultType] = useState([]);
  // const [appointmentStatus, setAppointmentStatus] = useState([])
  const [loading, setLoading] = useState(false);

  // États pour les onglets et sélection de graphes
  const [activeTab, setActiveTab] = useState('trend'); // 'trend' ou 'cancel'
  // Pour l'onglet trend on utilise 'line' et pour cancel on utilisera 'bar'
  const [selectedChart, setSelectedChart] = useState('line');
  // Les options du motif
  const [motif, setMotif] = useState('trend-activity');
  const [departments, setDepartments] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    department: "",
    specialty: "",
    period: "",
    customRange: { start: null, end: null },
    doctorId: doctor
  });

  const [showComparison, setShowComparison] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [comparisonYear, setComparisonYear] = useState(new Date().getFullYear() - 1);
  const [comparisonData, setComparisonData] = useState([]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [docRes] = await Promise.all([
          api.get("/api/usersprofile"),
        ]);

        setDepartments(docRes.data.departementId || []);
        setHospitals(docRes.data.hospital_id || []);

      } catch (err) {
        console.error("Error loading filters:", err);
      }
    };

    fetchFilters();
  }, []);

  const buildFilters = () => {
    const filters = {};
    if (filterOptions.hospital) filters.hospital = filterOptions.hospital;
    if (filterOptions.department) filters.department = filterOptions.department;
    if (filterOptions.specialty) filters.speciality = filterOptions.specialty;
    filters.doctorId = doctor;
    return filters;
  };

  const buildTimeRange = () => {
    const now = new Date();
    const todayStr = now.toISOString().split("T")[0];
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];

    if (filterOptions.period === "thisMonth") {
      return { start: firstDayOfMonth, end: todayStr };
    } else if (filterOptions.period === "last30") {
      const last30 = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
      return { start: last30, end: todayStr };
    } else if (filterOptions.period === "year") {
      const yearStart = new Date(now.getFullYear(), 0, 1).toISOString().split("T")[0];
      return { start: yearStart, end: todayStr };
    }
    else if (filterOptions.period === "custom") {
      return filterOptions.customRange;
    }
    return {};
  };

  // Modify the API call parameters to directly include the doctor ID

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const year = selectedYear;

        // Create base params that always include doctorId
        const baseParams = {
          // doctorId: doctor, // Always include doctor ID directly in params
          year: year,
          timeRange: JSON.stringify(buildTimeRange()),
          filters: JSON.stringify(buildFilters())
        };

        const [
          appointmentsRes,
          insightRes,
          patientsRes,
          distRes,
          appointmentSummaryRes,
          patientStatusRes,
          cancelledAppointmentRes,
          cancelledAppointmentByDepartmentRes,
          cancelledAppointmentByConsultTypeRes,
        ] = await Promise.all([
          api.get("/api/report/doctor/appointments", { params: baseParams }),
          api.get("/api/report/doctor/insights", { params: baseParams }),
          api.get("/api/report/doctor/patient-trends", { params: baseParams }),
          api.get("/api/report/doctor/distribution", { params: baseParams }),
          api.get("/api/report/doctor/summary", { params: baseParams }),
          api.get("/api/report/doctor/patient-status", { params: baseParams }),
          api.get("/api/report/doctor/cancelled/trend", { params: baseParams }),
          api.get("/api/report/doctor/cancelled/departments", { params: baseParams }),
          api.get("/api/report/doctor/cancelled/consult-type", { params: baseParams }),
        ]);

        const patientDataWithYear = patientsRes.data.data.map(item => ({
          ...item,
          year: selectedYear,
          label: `${item.month} ${selectedYear}`
        }));
        setAppointmentData(appointmentsRes.data.data);
        setInsights(insightRes.data.data);
        setPatientData(patientDataWithYear);
        setDistributionData(distRes.data.data);
        setAppointmentSummary(appointmentSummaryRes.data.data);
        setPatientStatus(patientStatusRes.data.data)
        setCancelledAppointment(cancelledAppointmentRes.data.data);
        setCancelledAppointmentByDepartment(cancelledAppointmentByDepartmentRes.data.data);
        setCancelledAppointmentByConsultType(cancelledAppointmentByConsultTypeRes.data.data);
      } catch (err) {
        console.error("Error fetching charts:", err);
      }
      setLoading(false);
    };

    fetchData();
  }, [filterOptions, selectedYear, doctor]); // Add doctor to dependency array

  const CRITICAL_COLORS = ['#FF5A5A', '#f0f0f0'];
  const SUCCESS_COLORS = ['#4BD7B2', '#f0f0f0'];



  // Données pour le LineChart de l'onglet trend
  const lineChartDataTrendActivity = appointmentData.map((entry, index) => ({
    month: entry.month,
    patient: patientData[index]?.y || 0,
    rdv: entry.value
  }));

  const pieChartDataSingle = distributionData.map(d => ({
    name: d.label,
    value: d.value
  }));


  // Définition des couleurs en fonction du motif et de l’onglet actif
  const motifColors = {
    'rdv-per-patient': { line: '#4bd7b2', pie: '#4bd7b2' },
    consultations: { line: '#f39c12', pie: '#f39c12' },
    interventions: { line: '#3498db', pie: '#3498db' },
    prescriptions: { line: '#9b59b6', pie: '#9b59b6' },
    'trend-activity': {
      line: { patient: '#2ecc71', rdv: '#e74c3c' },
      pie: { patient: '#2ecc71', rdv: '#e74c3c' },
    },
  };

  const cancelMotifColors = {
    'rdv-per-patient': { line: '#ff5a5a', pie: '#ff5a5a' },
    consultations: { line: '#ff8f66', pie: '#ff8f66' },
    interventions: { line: '#ffb366', pie: '#ffb366' },
    prescriptions: { line: '#ff9fdd', pie: '#ff9fdd' },
    'trend-activity': {
      line: { patient: '#ff5a5a', rdv: '#ff8f66' },
      pie: { patient: '#ff5a5a', rdv: '#ff8f66' },
    },
  };


  const statsCardData = [
    {
      title: "Total Patients",
      // value: (patientStatus.healthStatus || []).reduce((acc, p) => acc + p.count, 0),
      value: patientData.reduce((acc, p) => acc + p.y, 0),
      change: "+16%",
      changeType: "positive",
      iconColor: "icon-indigo",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      )
    },
    {
      title: "Total RDV",
      value: appointmentSummary.reduce((acc, a) => acc + a.count, 0),
      change: "+16%",
      changeType: "positive",
      iconColor: "icon-green",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      )
    },
    {
      title: "Total RDV en attente",
      value: appointmentSummary.find(a => a._id === "Scheduled")?.count || 0,
      change: "-18%",
      changeType: "negative",
      iconColor: "icon-red",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      )
    },
    {
      title: "Total Accouchement",
      value: patientStatus.totalDelivery?.count || 0,
      change: "-18%",
      changeType: "negative",
      iconColor: "icon-yellow",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      )
    },
    {
      title: "RDV Manqués",
      value: appointmentSummary.find(a => a._id === "Archived")?.count || 0,
      change: "-18%",
      changeType: "negative",
      iconColor: "icon-blue",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      )
    },


  ];

  const totalHealthCases = (patientStatus.healthStatus || []).reduce((acc, curr) => acc + curr.count, 0);
  const criticalCount = patientStatus.healthStatus?.find(h => h._id === "Critique")?.count || 0;
  const successCount = patientStatus.patientStatus?.find(h => h._id === "Guéri")?.count || 0;
  const criticalCaseData = [
    { name: "Critique", value: criticalCount },
    { name: "Autres", value: totalHealthCases - criticalCount }
  ];

  const successCaseData = [
    { name: "Stable", value: successCount },
    { name: "Autres", value: totalHealthCases - successCount }
  ];
  const chartCardData = [
    {
      label: "Critical Case",
      percentage: `${Math.round((criticalCount / totalHealthCases) * 100) || 0}%`,
      data: criticalCaseData,
      colors: CRITICAL_COLORS
    },
    {
      label: "Success Case",
      percentage: `${Math.round((successCount / totalHealthCases) * 100) || 0}%`,
      data: successCaseData,
      colors: SUCCESS_COLORS
    }
  ];


  const appointmentPieData = appointmentSummary.map(item => ({
    name: item.label,
    value: item.count
  }));

  const healthStatusPieData = (patientStatus.healthStatus || []).map(item => ({
    name: item.label,
    value: item.count
  }));


  const APPOINTMENT_COLORS = [
    'red', // Cancelled
    'orange', // Archived
    'green', // Completed
    'blue', // Scheduled
  ];
  const HEALTH_COLORS = ['#e53935', '#43a047', '#ffb300']; // Critique, Stable, Non critique


  const SimplePieChart = ({ data, colors, title }) => (
    <div className="chart-container" style={{ height: "inherit", marginBottom: "10px" }}>
      <PieChart width={520} height={280} >
        <Pie
          data={data}
          innerRadius={50}
          outerRadius={95}
          paddingAngle={2}
          dataKey="value"
          nameKey="name"
          label={({ name, percent }) => `${name} (${Math.round(percent * 100)}%)`}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend verticalAlign="bottom" height={36} />
      </PieChart>
      <h4 className="text-center font-semibold mb-2">{title}</h4>
    </div>
  );

  const COLORS = [
    '#0088FE', // Blue
    '#00C49F', // Teal
    '#FFBB28', // Yellow
    '#FF8042', // Orange
    '#A28BFE', // Violet
    '#FF4560', // Red
    '#6BD425', // Green
    '#3B9EFF', // Sky Blue
    '#EF476F', // Coral
    '#F78C6B'  // Peach
  ];


  // Sélection des couleurs selon l’onglet et le motif
  const colors =
    activeTab === 'trend'
      ? motifColors[motif] || { line: '#4bd7b2', pie: '#4bd7b2' }
      : cancelMotifColors[motif] || { line: '#ff5a5a', pie: '#ff5a5a' };

  // Indicateur pour savoir si le motif est "trend-activity"
  const isTrendActivity = motif === 'trend-activity';

  const hasGynecology = specialties?.some(s =>
    s.name?.toLowerCase().includes("gynéco") || s.name?.toLowerCase().includes("gyneco")
  );
  // console.log("hasGynecology", hasGynecology)

  // Add this function to fetch comparison data
  const fetchComparisonData = async () => {
    try {
      setLoading(true);

      // Create a copy of the current filters but with the comparison year
      const comparisonFilters = { ...buildFilters() };

      // Fetch comparison data
      const [appointmentsRes, patientsRes] = await Promise.all([
        api.get("/api/report/doctor/appointments", {
          params: {
            year: comparisonYear, // Explicitly pass the year parameter
            filters: JSON.stringify(comparisonFilters),
            timeRange: JSON.stringify(buildTimeRange())
          }
        }),
        api.get("/api/report/doctor/patient-trends", {
          params: {
            year: comparisonYear, // Explicitly pass the year parameter
            filters: JSON.stringify(comparisonFilters),
            timeRange: JSON.stringify(buildTimeRange())
          }
        })
      ]);

      // Process the comparison data with the comparison year
      const comparisonAppointmentData = appointmentsRes.data.data;
      const comparisonPatientData = patientsRes.data.data.map(item => ({
        ...item,
        year: comparisonYear,
        label: `${item.month} ${comparisonYear}`
      }));

      // Combine both appointment and patient data for comparison
      const combinedComparisonData = comparisonAppointmentData.map((entry, index) => ({
        month: entry.month,
        patient: comparisonPatientData[index]?.y || 0,
        rdv: entry.value,
        year: comparisonYear
      }));

      setComparisonData(combinedComparisonData);
    } catch (err) {
      console.error("Error fetching comparison data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Add toggle function for comparison
  const toggleComparison = () => {
    setShowComparison((prev) => !prev);
    if (!showComparison) {
      fetchComparisonData();
    }
  };

  // Add this effect to update comparison data when comparison year changes
  useEffect(() => {
    if (showComparison) {
      fetchComparisonData();
    }
  }, [comparisonYear, filterOptions]);

  // Add this to prepare data for the chart when comparison is active
  const currentYearData = lineChartDataTrendActivity.map(item => ({
    ...item,
    year: selectedYear
  }));

  const combinedChartData = showComparison
    ? [...currentYearData, ...comparisonData]
    : currentYearData;

  return (
    <div className="ins-dashboard">
    {/* HEADER */}
    <header className="ins-header">
      <h1 className="ins-title">Insight</h1>
    </header>

    {/* SECTION PRINCIPALE : 6 cartes & graphiques */}
    <section className="ins-main-section">
      <div className="ins-cards-grid">
        {/* Cartes statistiques */}
        {statsCardData
          .filter(card => {
            // Afficher toutes les cartes sauf quand la valeur est 0 ET le titre est "Total Accouchement"
            if (card.title === "Total Accouchement" && hasGynecology === false) return false;
            return true;
          })
          .map((card, index) => (
            <div key={index} className="ins-card">
              <div className={`ins-card-icon ins-icon-${card.iconColor.replace('icon-', '')}`}>
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  {card.icon}
                </svg>
              </div>
              <p className="ins-card-title">{card.title}</p>
              <div className="ins-card-stats">
                <span className="ins-stat-value">{card.value}</span>
                {card.change && (
                  <span className={`ins-stat-change-${card.changeType}`}>
                    {card.change}
                  </span>
                )}
              </div>
            </div>
          ))}

        {/* Cartes graphiques */}
        {chartCardData.map((chart, index) => (
          <div key={index} className="ins-chart-card">
            <div className="ins-chart-wrapper">
              <PieChart width={96} height={96}>
                <Pie
                  data={chart.data}
                  cx={48}
                  cy={48}
                  innerRadius={30}
                  outerRadius={42}
                  paddingAngle={0}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  {chart.data.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={chart.colors[i % chart.colors.length]} />
                  ))}
                </Pie>
              </PieChart>
              <div className="ins-chart-percentage">{chart.percentage}</div>
            </div>
            <p className="ins-chart-label">{chart.label}</p>
          </div>
        ))}
      </div>
    </section>

    {/* SECTION DES GRAPHIQUES AVEC ONGLETS */}
    <section className="ins-graph-section">
      {/* Onglets de navigation */}
      <div className="ins-tabs">
        <button
          className={`ins-tab-btn ${activeTab === 'trend' ? 'ins-tab-active' : ''}`}
          onClick={() => {
            setActiveTab('trend');
            setSelectedChart('line');
          }}
        >
          Tendances d'activités
        </button>
        <button
          className={`ins-tab-btn ${activeTab === 'cancel' ? 'ins-tab-cancel-active' : ''}`}
          onClick={() => {
            setActiveTab('cancel');
            setSelectedChart('bar');
          }}
        >
          Cas d'annulation de RDV
        </button>
      </div>

      {/* Contrôles pour le type de graphique et téléchargement */}
      <div className="ins-controls">
        <div className="ins-chart-controls">
          <button
            className={`ins-chart-btn ${selectedChart === (activeTab === 'cancel' ? 'bar' : 'line') ? 'ins-chart-btn-active' : ''}`}
            onClick={() => setSelectedChart(activeTab === 'cancel' ? 'bar' : 'line')}
          >
            {activeTab === 'cancel' ? 'Line Chart' : 'Bar Chart'}
          </button>
          <button
            className={`ins-chart-btn ${selectedChart === 'pie' ? 'ins-chart-btn-active' : ''}`}
            onClick={() => setSelectedChart('pie')}
          >
            Pie Chart
          </button>
        </div>
        
        <div className="ins-download-controls">
          <button className="ins-download-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download
          </button>

          <div className="ins-comparison">
            <button
              className={`ins-compare-toggle ${showComparison ? 'ins-compare-active' : ''}`}
              onClick={toggleComparison}
            >
              {showComparison ? 'Hide Comparison' : 'Compare Years'}
            </button>

            {showComparison && (
              <div className="ins-year-selectors">
                <div className="ins-year-group">
                  <label className="ins-year-label">Current Year:</label>
                  <select
                    className="ins-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>

                <div className="ins-year-group">
                  <label className="ins-year-label">Compare with:</label>
                  <select
                    className="ins-select"
                    value={comparisonYear}
                    onChange={(e) => setComparisonYear(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filtres complémentaires */}
      <div className="ins-filters">
        <select
          className="ins-filter-select"
          value={filterOptions.hospital}
          onChange={(e) => setFilterOptions(prev => ({ ...prev, hospital: e.target.value }))}
          aria-label="Filtre par hôpital"
        >
          <option value="">Hôpital</option>
          {hospitals.map((hosp) => (
            <option key={hosp._id} value={hosp._id}>{hosp.hospital_name}</option>
          ))}
        </select>

        <select
          className="ins-filter-select"
          value={filterOptions.period}
          onChange={(e) => setFilterOptions(prev => ({ ...prev, period: e.target.value }))}
          aria-label="Filtre par période"
        >
          <option value="thisMonth">Ce mois</option>
          <option value="lastMonth">Le mois dernier</option>
          <option value="year">Cette année</option>
          <option value="custom">Personnalisée</option>
        </select>
        
        {filterOptions.period === "custom" && (
          <div className="ins-date-range">
            <input
              type="date"
              className="ins-date-input"
              value={filterOptions.customRange.start}
              onChange={(e) => setFilterOptions(prev => ({ 
                ...prev, 
                customRange: { ...prev.customRange, start: e.target.value } 
              }))}
              aria-label="Date de début"
            />
            <input
              type="date"
              className="ins-date-input"
              value={filterOptions.customRange.end}
              onChange={(e) => setFilterOptions(prev => ({ 
                ...prev, 
                customRange: { ...prev.customRange, end: e.target.value } 
              }))}
              aria-label="Date de fin"
            />
          </div>
        )}

        <select
          className="ins-filter-select"
          value={filterOptions.department}
          onChange={(e) => setFilterOptions(prev => ({ ...prev, department: e.target.value }))}
          aria-label="Filtre par département"
        >
          <option value="">Département</option>
          {departments.map((d) => (
            <option key={d._id} value={d._id}>{d.name}</option>
          ))}
        </select>

        <button
          className="ins-reset-btn"
          // onClick={handleResetFilters}
        >
          Réinitialiser les filtres
        </button>
      </div>

      {/* Zone d'affichage des graphiques */}
      <div className="ins-chart-area">
        {/* Graphiques pour l'onglet Tendances */}
        {activeTab === 'trend' && (
          <>
            {selectedChart === 'line' && (
              <div className="ins-chart-container">
                {isTrendActivity ? (
                  showComparison ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis
                          dataKey="month"
                          type="category"
                          allowDuplicatedCategory={false}
                        />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => [value, name]}
                          labelFormatter={(label) => label}
                        />
                        <Legend />

                        {/* Données de l'année en cours */}
                        <Line
                          data={currentYearData}
                          type="monotone"
                          dataKey="rdv"
                          name={`Appointments ${selectedYear}`}
                          stroke="#e74c3c"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          data={currentYearData}
                          type="monotone"
                          dataKey="patient"
                          name={`Patients ${selectedYear}`}
                          stroke="#2ecc71"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />

                        {/* Données de l'année de comparaison */}
                        <Line
                          data={comparisonData}
                          type="monotone"
                          dataKey="rdv"
                          name={`Appointments ${comparisonYear}`}
                          stroke="#e74c3c"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          data={comparisonData}
                          type="monotone"
                          dataKey="patient"
                          name={`Patients ${comparisonYear}`}
                          stroke="#2ecc71"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <AppointmentPatientBarChart data={lineChartDataTrendActivity} />
                  )
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={patientData}>
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="y" 
                        stroke={colors.line} 
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}
            
            {selectedChart === 'pie' && (
              <div className="ins-pie-container">
                {isTrendActivity ? (
                  <>
                    <div className="ins-pie-item">
                      <ResponsiveContainer width="100%" height={300}>
                        <SimplePieChart
                          data={appointmentPieData}
                          colors={APPOINTMENT_COLORS}
                          title="Rendez-vous par statut"
                        />
                      </ResponsiveContainer>
                    </div>
                    <div className="ins-pie-item">
                      <ResponsiveContainer width="100%" height={300}>
                        <SimplePieChart
                          data={healthStatusPieData}
                          colors={HEALTH_COLORS}
                          title="État de santé des patients"
                        />
                      </ResponsiveContainer>
                    </div>
                  </>
                ) : (
                  <div className="ins-pie-item">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieChartDataSingle}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          fill={colors.pie}
                          label
                        >
                          {pieChartDataSingle.map((entry, i) => (
                            <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend verticalAlign="bottom" height={36} />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Graphiques pour l'onglet Annulations */}
        {activeTab === 'cancel' && (
          <>
            {selectedChart === 'bar' && (
              <div className="ins-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={cancelledAppointment}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      name="Rendez-vous annulés"
                      stroke="#f44336" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {selectedChart === 'pie' && (
              <div className="ins-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={cancelledAppointmentByDepartment}
                      dataKey="value"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {cancelledAppointmentByDepartment.map((entry, i) => (
                        <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend verticalAlign="bottom" height={36} />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  </div>

  );
}
